import { h } from 'preact';
import { ChangeEvent } from 'preact/compat';
import { useContext, useEffect, useState } from 'preact/hooks';
import { ToastServiceContext, ToastType } from '@uw/common-components';
import { headerStore } from 'global/header.store';
import { stateStore } from 'global/state.store';
import { useTranslation } from 'hooks';
import { usePasswordReset } from 'hooks/use-password-reset';
import { Button } from 'components/button';
import { FORGOT_PASSWORD_STATE, INITIAL_PAGE_STATE, NEW_LOGIN_STATE } from 'constants/constants';
import { RecoveryFormData } from './types';
import './recovery.scss';

export const Recovery = () => {
  const { translate } = useTranslation();
  const { showToast } = useContext(ToastServiceContext);
  const { resetPassword, resetErrors, clearResetError, isLoadingReset } = usePasswordReset();

  const [formData, setFormData] = useState<RecoveryFormData>({
    code: '',
    password: '',
    password_confirm: '',
  });

  const submitBtnTextPath = isLoadingReset
    ? 'widget.recovery.buttons.processing'
    : 'widget.recovery.buttons.submit';

  const handleFieldChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetFieldError = (e: ChangeEvent<HTMLInputElement>) => {
    const { name } = e.currentTarget;
    clearResetError(name as keyof typeof resetErrors);
  };

  const handleCancel = () => {
    stateStore.state.next(FORGOT_PASSWORD_STATE);
  };

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();

    const result = await resetPassword(formData, translate);

    if (result.success) {
      stateStore.state.next(NEW_LOGIN_STATE);
      headerStore.setBackStateForced(INITIAL_PAGE_STATE);
    } else if (result.message) {
      showToast(ToastType.Error, result.message);
    }
  };

  useEffect(() => {
    headerStore.backState.next(FORGOT_PASSWORD_STATE);
    headerStore.title.next('widget.recovery.title');
  }, []);

  return (
    <div className="uw-recovery">
      <form
        className="uw-recovery__form"
        name="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        <fieldset disabled={isLoadingReset}>
          <div
            className={`uw-recovery__form-group ${resetErrors.code ? 'uw-recovery__form-group_error' : ''} ${formData.code ? 'uw-recovery__form-group_filled' : ''}`}
          >
            <label htmlFor="code-field" id="recovery-code-label" className="uw-recovery__label">
              {translate('widget.recovery.placeholders.code')}
            </label>
            <input
              aria-labelledby="recovery-code-label"
              aria-describedby={resetErrors.code ? 'recovery-code-err' : undefined}
              id="code-field"
              name="code"
              className="uw-recovery__input-field"
              type="text"
              title={translate('widget.recovery.labels.code')}
              value={formData.code}
              onChange={handleFieldChange}
              onFocus={resetFieldError}
              aria-required="true"
              aria-invalid={!!resetErrors.code}
              autoComplete="one-time-code"
            />
          </div>
          {resetErrors.code && (
            <p id="recovery-code-err" role="alert" className="uw-recovery__help-block">
              {resetErrors.code}
            </p>
          )}

          <div
            className={`uw-recovery__form-group ${resetErrors.password ? 'uw-recovery__form-group_error' : ''} ${formData.password ? 'uw-recovery__form-group_filled' : ''}`}
          >
            <label htmlFor="password-field" id="recovery-pass-label" className="uw-recovery__label">
              {translate('widget.recovery.labels.password')}
            </label>
            <input
              id="password-field"
              name="password"
              className="uw-recovery__input-field"
              type="password"
              title={translate('widget.recovery.labels.password')}
              value={formData.password}
              onChange={handleFieldChange}
              onFocus={resetFieldError}
              aria-labelledby="recovery-pass-label"
              aria-describedby={resetErrors.password ? 'recovery-pass-err' : undefined}
              aria-required="true"
              aria-invalid={!!resetErrors.password}
              autoComplete="new-password"
            />
          </div>
          {resetErrors.password && (
            <p id="recovery-pass-err" role="alert" className="uw-recovery__help-block">
              {resetErrors.password}
            </p>
          )}

          <div
            className={`uw-recovery__form-group ${resetErrors.password_confirm ? 'uw-recovery__form-group_error' : ''} ${formData.password_confirm ? 'uw-recovery__form-group_filled' : ''}`}
          >
            <label
              htmlFor="password-confirm-field"
              id="recovery-pass-confirm-label"
              className="uw-recovery__label"
            >
              {translate('widget.recovery.labels.password_confirm')}
            </label>
            <input
              id="password-confirm-field"
              name="password_confirm"
              className="uw-recovery__input-field"
              type="password"
              title={translate('widget.recovery.labels.password_confirm')}
              value={formData.password_confirm}
              onChange={handleFieldChange}
              onFocus={resetFieldError}
              aria-labelledby="recovery-pass-confirm-label"
              aria-describedby={
                resetErrors.password_confirm ? 'recovery-pass-confirm-err' : undefined
              }
              aria-required="true"
              aria-invalid={!!resetErrors.password_confirm}
              autoComplete="new-password"
            />
          </div>
          {resetErrors.password_confirm && (
            <p id="recovery-pass-confirm-err" role="alert" className="uw-recovery__help-block">
              {resetErrors.password_confirm}
            </p>
          )}
        </fieldset>

        <div className="uw-recovery__btn-row">
          <Button
            color="white"
            className="uw-recovery__button"
            onClick={handleCancel}
            aria-label={translate('widget.recovery.arias.cancel')}
          >
            {translate('widget.recovery.buttons.cancel')}
          </Button>
          <Button
            className="uw-recovery__button"
            type="submit"
            aria-label={translate('widget.recovery.arias.submit')}
            aria-busy={isLoadingReset}
            disabled={isLoadingReset}
          >
            {translate(submitBtnTextPath)}
          </Button>
        </div>
      </form>
    </div>
  );
};
